export function titleLabel(value: string | null | undefined): string {
  const normalized = (value || '').trim().replace(/[-_]+/g, ' ');
  if (!normalized) return '';

  return normalized
    .split(/\s+/)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
}

export function getWorkspacePreview(
  preferred: string | null | undefined,
  fallback: string | null | undefined,
  maxLength = 72
): string {
  const text = (preferred || fallback || '').replace(/\s+/g, ' ').trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

export function formatCodexRelativeTime(
  value: string | null | undefined,
  now: Date = new Date()
): string {
  if (!value) return '-';
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return '-';

  const diffMs = Math.max(0, now.getTime() - timestamp);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;

  if (diffMs < minuteMs) return 'now';
  if (diffMs < hourMs) return `${Math.max(1, Math.floor(diffMs / minuteMs))}m`;
  if (diffMs < dayMs) return `${Math.max(1, Math.floor(diffMs / hourMs))}h`;
  if (diffMs < weekMs) return `${Math.max(1, Math.floor(diffMs / dayMs))}d`;
  if (diffMs < 5 * weekMs) return `${Math.max(1, Math.floor(diffMs / weekMs))}w`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export const WORKSPACE_BOARD_COLUMNS = [
  { key: 'open', label: 'Open', statuses: ['open', 'regression_candidate'] },
  { key: 'claimed', label: 'Claimed', statuses: ['claimed'] },
  { key: 'in_progress', label: 'In Progress', statuses: ['in_progress'] },
  { key: 'submitted', label: 'Submitted', statuses: ['submitted'] },
  { key: 'measuring', label: 'Measuring Impact', statuses: ['measuring'] },
  { key: 'verified', label: 'Verified', statuses: ['verified', 'closed'] },
  { key: 'dismissed', label: 'Dismissed', statuses: ['dismissed'] },
] as const;

export function buildWorkspaceBoardColumns<T extends { status: string }>(issues: T[]) {
  return WORKSPACE_BOARD_COLUMNS.map(column => ({
    ...column,
    issues: issues.filter(issue => column.statuses.includes(issue.status as never)),
  }));
}

export const GROWTH_TASK_PROGRESS_STEPS = [
  { key: 'discovered', label: 'Discovered' },
  { key: 'claimed', label: 'Claimed' },
  { key: 'in_progress', label: 'Working' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'measuring', label: 'Measuring Impact' },
  { key: 'verified', label: 'Verified' },
] as const;

export type GrowthTaskProgressStep = (typeof GROWTH_TASK_PROGRESS_STEPS)[number] & {
  state: 'complete' | 'active' | 'pending';
};

export type GrowthTaskCta = {
  label: string;
  action: 'claim' | 'work-log' | 'receipt' | 'measurement' | 'proof' | 'review';
};

type GrowthTaskStatusLike =
  | 'open'
  | 'claimed'
  | 'in_progress'
  | 'submitted'
  | 'measuring'
  | 'verified'
  | 'closed'
  | 'dismissed'
  | 'regression_candidate'
  | string;

type GrowthTaskLike = {
  status?: GrowthTaskStatusLike | null;
  updated_at?: string | null;
  latest_verification?: { outcome?: string | null } | null;
  latestVerification?: boolean | { outcome?: string | null } | null;
  outcome?: string | null;
};

const GROWTH_TASK_STATUS_STEP_INDEX: Record<string, number> = {
  open: 0,
  regression_candidate: 0,
  claimed: 1,
  in_progress: 2,
  submitted: 3,
  measuring: 4,
  verified: 5,
  closed: 5,
  dismissed: 0,
};

const CLOSED_GROWTH_STATUSES = new Set(['verified', 'closed']);

function growthTaskStatusRank(status: GrowthTaskStatusLike | null | undefined) {
  return GROWTH_TASK_STATUS_STEP_INDEX[String(status || 'open')] ?? 0;
}

function growthTaskOutcome(issue: GrowthTaskLike): string {
  return String(
    issue.latest_verification?.outcome ||
      (typeof issue.latestVerification === 'object' ? issue.latestVerification?.outcome : null) ||
      issue.outcome ||
      'unknown'
  );
}

function isVerifiedGrowthTaskOutcome(outcome: string): boolean {
  return outcome === 'helped' || outcome === 'no_movement' || outcome === 'regressed';
}

export function getGrowthTaskProgressSteps(
  status: GrowthTaskStatusLike | null | undefined
): GrowthTaskProgressStep[] {
  const activeIndex = growthTaskStatusRank(status);
  const isFinished = status === 'verified' || status === 'closed';

  return GROWTH_TASK_PROGRESS_STEPS.map((step, index) => ({
    ...step,
    state:
      isFinished || index < activeIndex ? 'complete' : index === activeIndex ? 'active' : 'pending',
  }));
}

export function getGrowthTaskCta(
  issue: GrowthTaskLike,
  options: { hasActiveAttempt?: boolean } = {}
): GrowthTaskCta {
  const hasVerification = Boolean(issue.latestVerification || issue.latest_verification);

  if (issue.status === 'open' || issue.status === 'regression_candidate') {
    return { label: 'Claim', action: 'claim' };
  }
  if (issue.status === 'in_progress' && options.hasActiveAttempt) {
    return { label: 'Submit receipt', action: 'receipt' };
  }
  if (issue.status === 'submitted') {
    return { label: 'Track measurement', action: 'measurement' };
  }
  if (issue.status === 'measuring') {
    return hasVerification
      ? { label: 'View proof', action: 'proof' }
      : { label: 'Track measurement', action: 'measurement' };
  }
  if (issue.status === 'verified' || issue.status === 'closed') {
    return { label: 'View proof', action: 'proof' };
  }
  if (issue.status === 'dismissed') {
    return { label: 'Review', action: 'review' };
  }
  return { label: 'Log progress', action: 'work-log' };
}

function parseDateMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function startOfUtcWeek(date: Date) {
  const start = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = start.getUTCDay();
  const daysSinceMonday = (day + 6) % 7;
  start.setUTCDate(start.getUTCDate() - daysSinceMonday);
  return start;
}

function utcWeekKey(timestamp: number) {
  const start = startOfUtcWeek(new Date(timestamp));
  return Math.floor(start.getTime() / (7 * 24 * 60 * 60 * 1000));
}

export function getGrowthWorkMomentum(issues: GrowthTaskLike[], options: { now?: Date } = {}) {
  const now = options.now || new Date();
  const weekStartMs = startOfUtcWeek(now).getTime();
  const currentWeekKey = utcWeekKey(now.getTime());
  const closureWeeks = new Set<number>();
  const impact = {
    helped: 0,
    no_movement: 0,
    regressed: 0,
    unknown: 0,
  };
  let closedThisWeek = 0;
  let verifiedThisWeek = 0;
  let measuring = 0;
  let inProgress = 0;

  for (const issue of issues) {
    const status = String(issue.status || '');
    const updatedMs = parseDateMs(issue.updated_at);
    const isClosure = CLOSED_GROWTH_STATUSES.has(status);
    const outcome = growthTaskOutcome(issue);
    const isVerifiedClosure =
      status === 'closed' || (status === 'verified' && isVerifiedGrowthTaskOutcome(outcome));

    if (status === 'measuring') measuring += 1;
    if (status === 'claimed' || status === 'in_progress' || status === 'submitted') {
      inProgress += 1;
    }
    if (isVerifiedClosure && updatedMs !== null) {
      closureWeeks.add(utcWeekKey(updatedMs));
      if (status === 'verified' && updatedMs >= weekStartMs) verifiedThisWeek += 1;
      if (status === 'closed' && updatedMs >= weekStartMs) closedThisWeek += 1;
    }
    if (isClosure && isVerifiedClosure) {
      if (outcome === 'helped') impact.helped += 1;
      else if (outcome === 'no_movement') impact.no_movement += 1;
      else if (outcome === 'regressed' || outcome === 'regression_candidate') impact.regressed += 1;
      else impact.unknown += 1;
    }
  }

  let weeklyStreak = 0;
  for (let weekKey = currentWeekKey; closureWeeks.has(weekKey); weekKey -= 1) {
    weeklyStreak += 1;
  }

  return {
    closedThisWeek,
    verifiedThisWeek,
    measuring,
    inProgress,
    weeklyStreak,
    impact,
  };
}

export type WorkspaceSessionLike = {
  id: string;
  isCurrent?: boolean;
};

function normalizeIdSet(values?: Iterable<string | null | undefined>) {
  const set = new Set<string>();
  if (!values) return set;
  for (const value of values) {
    const normalized = (value || '').trim();
    if (normalized) set.add(normalized);
  }
  return set;
}

export function filterWorkspaceChatSessions<T extends WorkspaceSessionLike>(
  sessions: T[],
  options: {
    agentThreadSessionIds?: Iterable<string | null | undefined>;
    accessibleSessionIds?: Iterable<string | null | undefined>;
  } = {}
) {
  const agentThreadSessionIds = normalizeIdSet(options.agentThreadSessionIds);
  const accessibleSessionIds = normalizeIdSet(options.accessibleSessionIds);
  const enforceAccess = Boolean(options.accessibleSessionIds);

  return sessions.filter(session => {
    const id = (session.id || '').trim();
    if (!id) return false;
    if (agentThreadSessionIds.has(id)) return false;
    if (enforceAccess && !accessibleSessionIds.has(id)) return false;
    return true;
  });
}
